import { BedDouble, Footprints, Snowflake, Tent, UtensilsCrossed, Zap } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { theme } from "./theme";
import type { Stop } from "./types";

/**
 * Tjenestene som får et merke på kartet, i den rekkefølgen de tegnes.
 *
 * Dataene lå der allerede — senger, camping, lading, spisesteder, skianlegg —
 * men bare bak et klikk per stopp. Da må man åpne elleve stopp etter tur for å
 * finne ut hvor man kan sove, og det er nettopp det spørsmålet man har når man
 * ser på ruta. Merkene svarer på det uten å åpne noe.
 *
 * Bare *om* tjenesten finnes, ikke hvor mange: seks tall på hvert punkt ville
 * gjort kartet til et regnskap, og det skal kunne leses på et blikk.
 *
 * Definisjonen bor her framfor i én av kartkomponentene fordi tre steder
 * trenger den — det skjematiske kartet, 3D-kartet og forklaringen under dem.
 * Sto den i det ene kartet, ville forklaringen kunnet love merker det andre
 * ikke tegner, og det var nettopp det som skjedde.
 */
export interface ServiceMark {
  key: "hike" | "lodging" | "camping" | "charging" | "food" | "ski";
  icon: LucideIcon;
  color: string;
  has: (stop: Stop) => boolean;
}

export const SERVICE_MARKS: ServiceMark[] = [
  // Fotturen først: den avgjør om stoppet i det hele tatt er en dagstur.
  {
    key: "hike",
    icon: Footprints,
    color: theme.color.accent,
    has: (stop) => Boolean(stop.hike),
  },
  {
    key: "lodging",
    icon: BedDouble,
    color: theme.color.fjord,
    has: (stop) =>
      (stop.lodging ?? []).some((entry) => entry.kind === "hotel" || entry.kind === "hostel"),
  },
  {
    key: "camping",
    icon: Tent,
    color: "#3F7A5E",
    has: (stop) =>
      (stop.lodging ?? []).some((entry) => entry.kind === "camping" || entry.kind === "cabin"),
  },
  {
    key: "charging",
    icon: Zap,
    color: "#2F6F8F",
    has: (stop) => (stop.amenities ?? []).some((a) => a.kind === "charging"),
  },
  {
    key: "food",
    icon: UtensilsCrossed,
    color: "#8A6A2F",
    has: (stop) => (stop.amenities ?? []).some((a) => a.kind === "food"),
  },
  {
    key: "ski",
    icon: Snowflake,
    color: "#5A7FA6",
    has: (stop) => Boolean(stop.ski && (stop.ski.lifts > 0 || stop.ski.hasDownhill)),
  },
];

/** Merkene et stopp faktisk har, i tegnerekkefølge. */
export function marksFor(stop: Stop): ServiceMark[] {
  return SERVICE_MARKS.filter((mark) => mark.has(stop));
}
